import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css, Style } from "@tento-ui/component-styles";
import { ContextMenu } from "@tento-ui/components/context-menu";
import { Divider } from "@tento-ui/components/divider";
import { PillButton } from "@tento-ui/components/pill-button";
import { Toast } from "@tento-ui/components/toast/toast";
import { GESTURE_MANAGER } from "@tento-ui/gesture-manager";
import { CalendarIcon, HashIcon, TrashIcon } from "@tento-ui/ui/icons";
import { AuthScopedCalendarId, CalendarsClient, type EventCategories, EventCategoryItem } from "@tento-lona";
import {
  Actor,
  EventItemAllDay,
  EventItemTimed,
  EventPatch,
  type Option,
} from "@tento-lona/sheets";
import type { EventsClient } from "@tento-lona";
import {
  RecurringEditModal,
  RecurringEditOption,
} from "@tento-ui/components/calendar/delete-recurring-event-modal";
import { RsvpStatusList } from "./context-menu/rsvp-status-list.ts";
import { EventWriteRefusal } from "./event-write-refusal.ts";
import type { EventAccess } from "../../../../sheet/gestures/event-access.ts";

/** The context menu of an event: tag, RSVP, copy and delete. */
export namespace EventContextMenu {
  /**
   * The menu offers what `access` (the opener's `EventAccess.of`) grants:
   * tags, RSVP and Delete, and copies to the account's other writable
   * calendars. An event whose calendar the account cannot write is offered
   * only its copies. `report` tells the user a write the menu issued was
   * refused (a host passes its calendar recovery).
   */
  export function create(
    event: EventItemTimed | EventItemAllDay,
    access: EventAccess,
    calendars: CalendarsClient,
    // Every write the menu issues (tag, RSVP, copy, delete) addresses the
    // held item through its `origin` — `row.events()` is read-only.
    events: EventsClient,
    onDismiss: () => void,
    report: EventWriteRefusal.Report = EventWriteRefusal.toast,
  ): ContextMenu.Built {
    const builder = ContextMenu.builder();
    if (access.tag) {
      builder
        .cellRow("Tags", HashIcon, null)
        .row(
          EventTagList.makeWith(events.categories, (tag) => {
            void EventWriteRefusal.attempt(event, "tag the event", report, () =>
              events.update(event, EventPatch.of({ category: tag.name }), Actor.EXTERNAL));
            $c.dismiss();
          }),
        )
        .row(Divider.make());
    }

    const targets = access.copy
      ? calendars.peekWritableExcept(AuthScopedCalendarId.ofEvent(event.inner), 3)
      : [];

    if (targets.length > 0) {
      for (const targetCalendar of targets) {
        builder.cellRow(
          `Copy to ${targetCalendar.summary}`,
          CalendarIcon,
          async () => {
            // Held-item copy through `EventsClient.copyTo`, which resolves the
            // source row via `event.origin` (a just-created event may not be in
            // this row's reader yet) — same path as the context-menu delete.
            const copied = await EventWriteRefusal.attempt(event, "copy the event", report, () =>
              events.copyTo(event, targetCalendar, Actor.EXTERNAL));
            if (copied) Toast.info(`Event copied to ${targetCalendar.summary}`);
            onDismiss();
            $c.dismiss();
          },
        );
      }
      builder.row(Divider.make());
    }

    if (access.rsvp && event instanceof EventItemTimed) {
      const attendee = event.getAttendee();
      if (attendee) {
        const $rsvpList = RsvpStatusList.makeWith(async (status: string) => {
          const answered = await EventWriteRefusal.attempt(event, "answer the invitation", report, () =>
            events.update(event, EventPatch.of({ responseStatus: status }), Actor.EXTERNAL));
          if (answered && attendee.responseStatus != null) {
            attendee.responseStatus = status;
          }
          $c.dismiss();
        });
        $rsvpList.style.padding = "0px 0px";
        builder.row($rsvpList).row(Divider.make());
      }
    }

    if (access.delete) {
      builder.cellRow("Delete", TrashIcon, () => {
        void showDeleteEventPrompt(event, events, onDismiss, report);
      });
    }
    const $c = builder.build({
      onDismiss,
    });

    $c.style.width = "200px";
    GESTURE_MANAGER.addBarrier($c);
    return $c;
  }

  export async function showDeleteEventPrompt(
    event: EventItemTimed | EventItemAllDay,
    events: Pick<EventsClient, "delete" | "deleteOccurrence">,
    onDismiss: () => void,
    report: EventWriteRefusal.Report = EventWriteRefusal.toast,
  ) {
    const recurrenceInfo = event.recurrenceInfo;
    if (!recurrenceInfo) {
      // Non-recurring: delete the held item, resolved via its `origin`.
      // Routing through `EventsClient` (not the rendering row's `events()`)
      // attaches the data-owning source row first — the rendering row may be
      // a calendar-list aggregate projection that owns no cells. The delete
      // paints first and puts the event back when it is refused; the refusal
      // (with its reason) is reported, never left floating.
      void EventWriteRefusal.attempt(event, "delete the event", report, () =>
        events.delete(event, Actor.EXTERNAL));
      onDismiss();
      return;
    }

    const eventTitle = event.inner.title || "Untitled Event";
    await RecurringEditModal.show(
      "delete",
      eventTitle,
      async (option: RecurringEditOption) => {
        await EventWriteRefusal.attempt(event, "delete the event", report, () =>
          events.deleteOccurrence(event, option, Actor.EXTERNAL));
        onDismiss();
      },
    );
  }
}

@component({
  name: "lona-event-tag-list",
})
export class EventTagList extends LonaWebComponent {
  static readonly TAGS: readonly string[] = ["Exercise", "Food", "Shower", "Sleep", "Work", "Chores"];

  /** A pill per category of `categories` (`client.events.categories`), in its colours. */
  static makeWith(
    categories: EventCategories,
    onSelectTag: (tag: EventCategoryItem) => void,
  ): EventTagList {
    const $e = EventTagList.make();
    for (const tag of categories.all()) {
      const $pill = PillButton.makeWith(tag.name, {
        background: tag.colorOpt() ?? "var(--gray)",
        ...PillButton.Options.SMALL,
      });
      $pill.style.cursor = "pointer";
      GESTURE_MANAGER.addPointerEvent($pill, {
        onClick: () => onSelectTag(tag),
      });
      $e.appendChild($pill);
    }
    return $e;
  }

  static $styles: Style.Compat[] = [
    css`
      #root {
        padding: 4px;
      }

      #tags {
        flex-wrap: wrap;
        margin-bottom: -4px;
        margin-right: -4px;
      }

      slot::slotted(*) {
        margin-bottom: 4px;
        margin-right: 4px;
      }

      #label {
        --margin-bottom: 8px;
      }
    `,
  ];

  static $html: Option<HTMLTemplateElement> = html`
    <std-col id="root">
      <std-row id="tags">
        <slot></slot>
      </std-row>
    </std-col>
  `;
}
